import React, { useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faCopy,
  faFolder,
  faGlobe,
  faTrashCan,
  faUser
} from '@fortawesome/pro-light-svg-icons';
import { trpc } from '@/utils/trpc-client';
import { SavedView, TableType } from 'shared/src/domain/saved-view/saved-view-types';
import { Modal } from '../feedback/modal';
import { AlertButton, OutlineButton, SecondaryButton } from '../display/button';
import clsx from 'clsx';
import { useUserDetails } from '@/auth/use-auth';
import { useFlagEnabled } from '@/utils/feature-flags';
import { handleViewLoad } from '@/components/tables/saved-views-utils';

type Props = {
  tableType: TableType;
};

export function SavedViewsDropdown({ tableType }: Props) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState<{ id: string; name: string } | null>(null);
  const [copiedViewId, setCopiedViewId] = useState<string | null>(null);
  const canDeleteGlobalViews = useFlagEnabled('saved-views-create-global');
  const { userDetails } = useUserDetails();

  const { data: views = [], refetch } = trpc.savedViews.useQuery({ tableType });
  const deleteViewMutation = trpc.deleteSavedView.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteModalOpen(false);
      setViewToDelete(null);
    }
  });

  const personalViews = views.filter(view => view.scope === 'personal');
  const globalViews = views.filter(view => view.scope === 'global');

  function handleViewSelect(viewState: Record<string, any>) {
    handleViewLoad(viewState);
  }

  function canDeleteView(view: SavedView): boolean {
    if (!userDetails) return false;
    // TODO [ac] need the concept of roles to allow admin users in the front end (dont want to hardcode emails).
    //  Admins should be allowed delete global views
    if (view.scope === 'personal') {
      return true;
    } else if (view.scope === 'global') {
      return canDeleteGlobalViews || false;
    }
    return false;
  }

  function handleDeleteClick(e: React.MouseEvent, viewId: string, viewName: string) {
    e.stopPropagation();
    setViewToDelete({ id: viewId, name: viewName });
    setDeleteModalOpen(true);
  }

  function handleCopyShortcode(e: React.MouseEvent, shortCode: string, viewId: string) {
    e.stopPropagation();
    const url = `${window.location.origin}/v/${shortCode}`;
    navigator.clipboard.writeText(url);
    setCopiedViewId(viewId);
    setTimeout(() => setCopiedViewId(null), 2000);
  }

  function handleConfirmDelete() {
    if (viewToDelete) {
      deleteViewMutation.mutate({ id: viewToDelete.id });
    }
  }

  if (views.length === 0) {
    return null;
  }

  return (
    <>
      <Menu as="div" className="relative inline-block w-40 text-left">
        <MenuButton as="div">
          <OutlineButton onClick={() => {}}>
            <FontAwesomeIcon icon={faFolder} />
            <div className="whitespace-nowrap">Saved Views</div>
            <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
          </OutlineButton>
        </MenuButton>

        <MenuItems className="absolute left-0 z-10 mt-2 w-64  origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
          <div className="py-1 max-h-[70vh] overflow-y-auto">
            {globalViews.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                  Global Views
                </div>
                {globalViews.map(view => (
                  <ViewMenuItem
                    key={view.id}
                    view={view}
                    isCopied={copiedViewId === view.id}
                    onSelect={() => handleViewSelect(view.view_state)}
                    onCopy={e => handleCopyShortcode(e, view.short_code, view.id)}
                    onDelete={
                      canDeleteView(view)
                        ? e => handleDeleteClick(e, view.id, view.name)
                        : undefined
                    }
                  />
                ))}
                {personalViews.length > 0 && <hr className="my-1" />}
              </>
            )}

            {personalViews.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <FontAwesomeIcon icon={faUser} className="mr-1" />
                  Personal Views
                </div>
                {personalViews.map(view => (
                  <ViewMenuItem
                    key={view.id}
                    view={view}
                    isCopied={copiedViewId === view.id}
                    onSelect={() => handleViewSelect(view.view_state)}
                    onCopy={e => handleCopyShortcode(e, view.short_code, view.id)}
                    onDelete={
                      canDeleteView(view)
                        ? e => handleDeleteClick(e, view.id, view.name)
                        : undefined
                    }
                  />
                ))}
              </>
            )}
          </div>
        </MenuItems>
      </Menu>

      <Modal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        header={
          <div className="flex flex-col gap-3 rounded-t-2xl bg-red-100 py-6 text-center text-2xl font-semibold text-red-600">
            <FontAwesomeIcon icon={faTrashCan} />
            Delete Saved View?
          </div>
        }
        content={
          <div className="p-4 text-sm text-gray-700">
            <div className="font-semibold">Delete "{viewToDelete?.name}"</div>
            <div>This action cannot be undone.</div>
          </div>
        }
        footer={
          <div className="flex gap-4 p-4">
            <SecondaryButton
              className="grow justify-around"
              onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </SecondaryButton>
            <AlertButton
              className="grow justify-around"
              onClick={handleConfirmDelete}
              disabled={deleteViewMutation.isPending}>
              {deleteViewMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertButton>
          </div>
        }
      />
    </>
  );
}

type ViewMenuItemProps = {
  view: { id: string; name: string; scope: string };
  isCopied: boolean;
  onSelect: () => void;
  onCopy: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
};

function ViewMenuItem({ view, isCopied, onSelect, onCopy, onDelete }: ViewMenuItemProps) {
  return (
    <MenuItem>
      <div className="group flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
        <button onClick={onSelect} className="flex-1 truncate text-left" title={view.name}>
          {view.name}
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={onCopy}
            className={clsx(
              'p-1 opacity-0 transition-all duration-200 focus:opacity-100 focus:outline-none group-hover:opacity-100',
              isCopied ? 'scale-110 text-green-500' : 'text-gray-400 hover:text-blue-500'
            )}
            title={isCopied ? 'Copied!' : 'Copy shortcode URL'}>
            <FontAwesomeIcon
              icon={isCopied ? faCheck : faCopy}
              className={clsx(
                'h-3 w-3 transition-transform duration-200',
                isCopied && 'animate-pulse'
              )}
            />
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className={clsx(
                'p-1 text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100',
                'focus:opacity-100 focus:outline-none'
              )}
              title="Delete view">
              <FontAwesomeIcon icon={faTrashCan} className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </MenuItem>
  );
}
